import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";
import { Card } from "./ui/card";
import { TrendingUp, Loader2 } from "lucide-react";
import { dashboard as dashboardApi } from "../lib/api";

/**
 * Weekly / bi-weekly activity chart for the admin dashboard.
 * Shows per-day counts of opened / in_progress / completed / cannot_do
 * grouped as stacked bars.
 */
export default function ActivityChartCard({ viewAll }) {
  const { t, i18n } = useTranslation();
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (nDays = days) => {
    setLoading(true);
    try {
      const r = await dashboardApi.activityChart({ days: nDays, view_all: viewAll });
      setData(r);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, viewAll]);

  // Compact date label matching the user's locale (Mon 12, Tue 13…)
  const fmt = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(i18n.language || "en", { month: "short", day: "numeric" });
    } catch {
      return { format: (d) => d.toString().slice(5) };
    }
  }, [i18n.language]);
  const series = useMemo(
    () =>
      (data?.series || []).map((row) => ({
        ...row,
        label: fmt.format(new Date(row.date + "T00:00:00Z")),
      })),
    [data, fmt],
  );

  const totals = data?.totals || { opened: 0, completed: 0, in_progress: 0, cannot_do: 0 };

  return (
    <Card className="canales-card p-6 mt-5" data-testid="section-activity">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="canales-overline flex items-center gap-1.5">
            <TrendingUp size={12} /> {t("activity_chart.overline", "Weekly activity")}
          </div>
          <h3 className="font-display text-xl md:text-2xl text-stone-900 tracking-tight mt-1">
            {t("activity_chart.title", "TO DO activity")}
          </h3>
          <p className="text-xs text-stone-500 mt-0.5">
            {t("activity_chart.subtitle", "Opened vs. resolved per day.")}
          </p>
        </div>
        {/* Range toggle */}
        <div className="inline-flex rounded-full border border-stone-200 p-0.5 bg-white" data-testid="activity-range-toggle">
          {[7, 14, 30].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setDays(n)}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                days === n ? "bg-[#0B1220] text-white" : "text-stone-600 hover:text-stone-900"
              }`}
              data-testid={`activity-range-${n}`}
            >
              {n}d
            </button>
          ))}
        </div>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-4 gap-2 mt-4">
        <TotalPill label={t("activity_chart.opened", "Opened")}     value={totals.opened}       color="#0B1220" />
        <TotalPill label={t("activity_chart.in_progress", "Started")}  value={totals.in_progress}  color="#F59E0B" />
        <TotalPill label={t("activity_chart.completed", "Completed")} value={totals.completed}    color="#10B981" />
        <TotalPill label={t("activity_chart.cannot_do", "Cannot do")} value={totals.cannot_do}    color="#EF4444" />
      </div>

      {/* Chart */}
      <div className="mt-4 relative" style={{ height: 260 }}>
        {loading && (
          <div className="absolute inset-0 grid place-items-center bg-white/60 z-10">
            <Loader2 className="animate-spin text-stone-400" size={22} />
          </div>
        )}
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={series} margin={{ top: 8, right: 8, left: -12, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={{ stroke: "#E5E7EB" }} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={{ stroke: "#E5E7EB" }} tickLine={false} />
            <Tooltip
              contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }}
              cursor={{ fill: "rgba(15, 23, 42, 0.04)" }}
              labelFormatter={(v) => v}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="opened"       name={t("activity_chart.opened", "Opened")}       stackId="a" fill="#0B1220" radius={[0, 0, 0, 0]} />
            <Bar dataKey="in_progress"  name={t("activity_chart.in_progress", "Started")} stackId="a" fill="#F59E0B" radius={[0, 0, 0, 0]} />
            <Bar dataKey="completed"    name={t("activity_chart.completed", "Completed")} stackId="a" fill="#10B981" radius={[6, 6, 0, 0]} />
            <Bar dataKey="cannot_do"    name={t("activity_chart.cannot_do", "Cannot do")} stackId="a" fill="#EF4444" radius={[0, 0, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function TotalPill({ label, value, color }) {
  return (
    <div className="rounded-lg border border-stone-200 bg-white px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold flex items-center gap-1.5">
        <span className="inline-block w-2 h-2 rounded-full" style={{ background: color }} />
        {label}
      </div>
      <div className="text-lg font-semibold tabular-nums mt-0.5 text-stone-900">{value}</div>
    </div>
  );
}
